import { createHash } from "node:crypto";
import type {
  DispatchScriptContentType,
  DispatchScriptSlotKind,
  ParsedDispatchScriptRow,
} from "./dispatchScriptSheet";
import type { DispatchScriptMatchStatus } from "./dispatchScriptTime";

export interface DispatchScriptSnapshotRowTime {
  raw: string;
  inherited: boolean;
  hour?: number;
  minute?: number;
  display?: string;
  totalMinutes?: number;
  parseError?: string;
}

export interface DispatchScriptSnapshotRow {
  rowIndex: number;
  rowNumber: number;
  time: DispatchScriptSnapshotRowTime;
  role: string;
  roleKind: "dispatcher" | "atmosphere";
  roleIsInherited: boolean;
  text: string;
  imageFileToken?: string;
  contentTypes: DispatchScriptContentType[];
  slots: DispatchScriptSlotKind[];
  windowStartMinutes: number;
  windowEndMinutes: number;
  hitRoomIds: string[];
  extraRoomIds: string[];
  hitRoomMessageCounts?: Record<string, number>;
  hitRoomFirstMinutes?: Record<string, number>;
  messageCount: number;
  matchStatus: DispatchScriptMatchStatus;
}

/** 匹配窗口：内容优先，剧本预设时间前后各 60 分钟（含两端）。 */
export const DISPATCH_SCRIPT_MATCH_TOLERANCE_MINUTES = 60;

const DAY_END_MINUTES = 24 * 60;

/**
 * 以剧本标注时刻为中心，前后各 {@link DISPATCH_SCRIPT_MATCH_TOLERANCE_MINUTES} 分钟。
 * 返回半开区间 [start, end)。
 */
export function getDispatchScriptMatchWindow(presetMinutes: number): {
  windowStartMinutes: number;
  windowEndMinutes: number;
} {
  const safe = Math.max(0, Math.min(DAY_END_MINUTES - 1, Math.floor(presetMinutes)));
  return {
    windowStartMinutes: Math.max(0, safe - DISPATCH_SCRIPT_MATCH_TOLERANCE_MINUTES),
    windowEndMinutes: Math.min(
      DAY_END_MINUTES,
      safe + DISPATCH_SCRIPT_MATCH_TOLERANCE_MINUTES + 1
    ),
  };
}

/** 剧本行的预设分钟数（用于监测起点判定与匹配窗中心） */
export function getDispatchScriptRowPresetMinutes(row: {
  time?: { totalMinutes?: number | null };
  windowStartMinutes?: number;
}): number {
  if (typeof row.time?.totalMinutes === "number" && Number.isFinite(row.time.totalMinutes)) {
    return row.time.totalMinutes;
  }
  if (typeof row.windowStartMinutes === "number" && Number.isFinite(row.windowStartMinutes)) {
    return row.windowStartMinutes;
  }
  return 0;
}

export function buildDispatchScriptRowWindows(
  rows: ParsedDispatchScriptRow[]
): Array<ParsedDispatchScriptRow & { windowStartMinutes: number; windowEndMinutes: number }> {
  return rows.map((row) => {
    const preset =
      typeof row.time.totalMinutes === "number" && Number.isFinite(row.time.totalMinutes)
        ? row.time.totalMinutes
        : null;
    if (preset == null) {
      return {
        ...row,
        windowStartMinutes: 0,
        windowEndMinutes: DAY_END_MINUTES,
      };
    }
    const window = getDispatchScriptMatchWindow(preset);
    return {
      ...row,
      windowStartMinutes: window.windowStartMinutes,
      windowEndMinutes: window.windowEndMinutes,
    };
  });
}

export function buildDispatchScriptSourceFingerprint(
  input: {
    spreadsheetToken: string;
    sheetId: string;
    rows: Array<{
      rowNumber: number;
      role: string;
      text: string;
      imageFileToken?: string;
      contentTypes: string[];
      slots: string[];
      timeDisplay?: string;
      windowStartMinutes: number;
      windowEndMinutes: number;
    }>;
  }
): string {
  const payload = JSON.stringify({
    spreadsheetToken: input.spreadsheetToken,
    sheetId: input.sheetId,
    rows: input.rows.map((row) => ({
      rowNumber: row.rowNumber,
      role: row.role,
      text: row.text,
      imageFileToken: row.imageFileToken ?? "",
      contentTypes: row.contentTypes,
      slots: row.slots,
      timeDisplay: row.timeDisplay ?? "",
      windowStartMinutes: row.windowStartMinutes,
      windowEndMinutes: row.windowEndMinutes,
    })),
  });
  return createHash("sha256").update(payload).digest("hex");
}

export function emptyMatchStats() {
  return {
    totalRows: 0,
    pending: 0,
    exact: 0,
    short: 0,
    excess: 0,
    skipped: 0,
  };
}

export function recountMatchStats(rows: Array<{ matchStatus: DispatchScriptMatchStatus }>) {
  const stats = emptyMatchStats();
  stats.totalRows = rows.length;
  for (const row of rows) {
    stats[row.matchStatus] += 1;
  }
  return stats;
}
